/**
 * ChatbotSessions — 챗봇 대화 로그 + 라이브 채팅 어드민 콘솔
 *
 * - 좌: 세션 리스트(필터 + 페이지네이션). live 필터로 대기/응대중/종료 분리 표시
 * - 우: 선택된 세션 상세
 *     · 라이브 (escalated + 미종료) → LiveChatPanel (3초 폴링 + 메시지 송신 + 종료)
 *     · 그 외 → ChatTranscript (읽기 전용 transcript)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { apiFetch } from '@/lib/api'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Spinner } from '../ui/Spinner'
import { Badge } from '../ui/Badge'
import { Pagination } from '../ui/Pagination'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'
import s from './ChatbotSessions.module.css'

type MessageRole = 'user' | 'assistant' | 'agent' | 'system'

interface SessionSummary {
  id: string
  userId: string
  userEmail: string | null
  startedAt: string
  endedAt: string | null
  escalatedAt: string | null
  escalatedInquiryId: string | null
  agentJoinedAt: string | null
  totalInputTokens: number
  totalOutputTokens: number
  totalCacheReadTokens: number
  totalCacheWriteTokens: number
  messageCount: number
}

interface SessionsResponse {
  items: SessionSummary[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

interface SessionDetail {
  session: SessionSummary & { userName: string | null }
  messages: Array<{
    id: string
    role: MessageRole
    content: Array<
      | { type: 'text'; text: string }
      | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
      | { type: 'tool_result'; tool_use_id: string; content: string; is_error?: boolean }
    >
    modelStopReason: string | null
    createdAt: string
  }>
}

interface LiveMessage {
  id: string
  role: MessageRole
  text: string
  createdAt: string
}

interface PollResponse {
  messages: LiveMessage[]
  agentJoinedAt: string | null
  endedAt: string | null
}

function formatTokens(tokens: number) {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`
  return String(tokens)
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function avatarInitial(email: string | null) {
  if (!email) return '?'
  return email.charAt(0).toUpperCase()
}

function formatToolInput(input: Record<string, unknown>) {
  try {
    const json = JSON.stringify(input, null, 2)
    return json.length > 600 ? `${json.slice(0, 600)}…` : json
  } catch {
    return String(input)
  }
}

function liveStatus(session: { escalatedAt: string | null; agentJoinedAt: string | null; endedAt: string | null }):
  | 'chatbot'
  | 'waiting'
  | 'active'
  | 'ended' {
  if (!session.escalatedAt) return 'chatbot'
  if (session.endedAt) return 'ended'
  if (!session.agentJoinedAt) return 'waiting'
  return 'active'
}

const LIVE_STATUS_LABEL: Record<'chatbot' | 'waiting' | 'active' | 'ended', { label: string; color: 'gray' | 'red' | 'green' | 'blue' }> = {
  chatbot: { label: '챗봇', color: 'gray' },
  waiting: { label: '대기 중', color: 'red' },
  active: { label: '상담 중', color: 'green' },
  ended: { label: '종료', color: 'gray' },
}

export function ChatbotSessions() {
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [live, setLive] = useState<'' | 'waiting' | 'active' | 'ended' | 'any-live'>('any-live')
  const [userIdFilter, setUserIdFilter] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', String(pageSize))
    if (live) p.set('live', live)
    if (userIdFilter) p.set('userId', userIdFilter)
    return p.toString()
  }, [page, pageSize, live, userIdFilter])

  const { data, isLoading, refetch: refetchList } = useApiFetch<SessionsResponse>(
    `/api/admin/chatbot-sessions?${queryString}`,
  )

  const { data: detail, refetch: refetchDetail } = useApiFetch<SessionDetail>(
    selectedId ? `/api/admin/chatbot-sessions/${selectedId}` : null,
  )

  // 페이지/필터 변경 시 선택 초기화
  useEffect(() => {
    setSelectedId(null)
  }, [page, live, userIdFilter])

  // 활성 세션 리스트는 30초마다 새로고침 — 새 대기 세션 인지용
  useEffect(() => {
    if (live === 'waiting' || live === 'any-live' || live === 'active') {
      const t = setInterval(() => refetchList(), 30_000)
      return () => clearInterval(t)
    }
  }, [live, refetchList])

  const items = data?.items ?? []
  const pagination = data?.pagination

  const columns: TableColumn<SessionSummary>[] = [
    {
      key: 'startedAt',
      label: '시작',
      width: '140px',
      render: (row) => fmtDate(row.startedAt),
    },
    {
      key: 'status',
      label: '상태',
      width: '90px',
      render: (row) => {
        const st = liveStatus(row)
        const info = LIVE_STATUS_LABEL[st]
        return <Badge color={info.color}>{info.label}</Badge>
      },
    },
    {
      key: 'userEmail',
      label: '사용자',
      render: (row) => row.userEmail ?? '(unknown)',
    },
    {
      key: 'messageCount',
      label: '메시지',
      width: '70px',
      align: 'center',
      render: (row) => row.messageCount,
    },
  ]

  const liveOptions = [
    { value: 'any-live', label: '라이브 (대기+상담 중)' },
    { value: 'waiting', label: '대기 중' },
    { value: 'active', label: '상담 중' },
    { value: 'ended', label: '라이브 종료됨' },
    { value: '', label: '전체 (챗봇 포함)' },
  ]

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 80px)' }}>
      {/* 좌측 — 리스트 */}
      <div style={{ flex: '1 1 55%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>챗봇 대화 / 라이브 상담</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--ds-color-text-secondary)', fontSize: 13 }}>
            대기 중/상담 중 세션은 우측 패널에서 직접 응대합니다.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 240px' }}>
            <span style={{ fontSize: 12, color: 'var(--ds-color-text-secondary)' }}>상태</span>
            <Select
              value={live}
              onChange={(e) => {
                setPage(1)
                setLive(e.target.value as typeof live)
              }}
              options={liveOptions}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 280px' }}>
            <span style={{ fontSize: 12, color: 'var(--ds-color-text-secondary)' }}>사용자 ID</span>
            <Input
              value={userIdFilter}
              onChange={(e) => {
                setPage(1)
                setUserIdFilter(e.target.value)
              }}
              placeholder="cuid2 ID"
            />
          </label>
        </div>

        <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
          <AdminTable
            data={items}
            columns={columns}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            emptyMessage="세션이 없습니다"
            onRowClick={(row) => setSelectedId(row.id)}
            hoverable
          />
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div style={{ paddingTop: 12 }}>
            <Pagination current={pagination.page} total={pagination.totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {/* 우측 — 상세 */}
      <aside style={{ flex: '1 1 45%', minWidth: 380, display: 'flex', flexDirection: 'column' }}>
        {!selectedId && (
          <div className={s.chatPanel}>
            <div className={s.emptyState}>세션을 선택하세요.</div>
          </div>
        )}
        {selectedId && !detail && (
          <div className={s.chatPanel}>
            <div className={s.emptyState}>
              <Spinner />
            </div>
          </div>
        )}
        {detail &&
          (liveStatus(detail.session) === 'chatbot' || liveStatus(detail.session) === 'ended' ? (
            <ChatTranscript detail={detail} />
          ) : (
            <LiveChatPanel
              key={detail.session.id}
              detail={detail}
              onChanged={() => {
                refetchList()
                refetchDetail()
              }}
            />
          ))}
      </aside>
    </div>
  )
}

// ============================================================
// LiveChatPanel — 라이브 채팅(대기 중 / 상담 중) 어드민용 패널
// ============================================================
// - 3초 폴링으로 새 메시지 수신
// - composer 로 메시지 전송 (Enter 송신, Shift+Enter 줄바꿈)
// - 종료 버튼 (확인 후 POST /end)
// - 새 사용자 메시지 도착 시 문서 타이틀에 (n) prefix

const POLL_INTERVAL_MS = 3000

function LiveChatPanel({ detail, onChanged }: { detail: SessionDetail; onChanged: () => void }) {
  const { session } = detail
  const [messages, setMessages] = useState<LiveMessage[]>([])
  const [composer, setComposer] = useState('')
  const [sending, setSending] = useState(false)
  const [ending, setEnding] = useState(false)
  const [agentJoinedAt, setAgentJoinedAt] = useState<string | null>(session.agentJoinedAt)
  const [endedAt, setEndedAt] = useState<string | null>(session.endedAt)
  const [pollError, setPollError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const lastCursorRef = useRef<string | null>(null)
  const originalTitleRef = useRef<string>(document.title)
  const unreadRef = useRef(0)

  // 초기 로딩 + 폴링
  // 탭이 hidden 일 때는 폴링 중단 (Lambda 인보케이션 절감). visible 로 돌아오면 즉시 1회 폴링 후 재개 +
  // 언리드 타이틀 카운트 초기화.
  useEffect(() => {
    let cancelled = false
    let timer: number | null = null
    let initialDone = false

    async function poll(initial: boolean) {
      try {
        const since = lastCursorRef.current
        const qs = since ? `?since=${encodeURIComponent(since)}` : ''
        const res = await apiFetch<PollResponse>(
          `/api/admin/chatbot-sessions/${session.id}/messages${qs}`,
        )
        if (cancelled) return
        if (res.messages.length > 0) {
          lastCursorRef.current = res.messages[res.messages.length - 1].createdAt
          setMessages((prev) => (initial ? res.messages : [...prev, ...res.messages]))
          // 새 사용자 메시지 카운트
          const newUserMessages = initial ? 0 : res.messages.filter((m) => m.role === 'user').length
          if (newUserMessages > 0 && document.visibilityState !== 'visible') {
            unreadRef.current += newUserMessages
            document.title = `(${unreadRef.current}) ${originalTitleRef.current}`
          }
        }
        setAgentJoinedAt(res.agentJoinedAt)
        setEndedAt(res.endedAt)
        setPollError(null)
      } catch (err) {
        if (!cancelled) setPollError(err instanceof Error ? err.message : '폴링 실패')
      }
    }

    const start = () => {
      if (timer !== null) return
      // 첫 진입은 initial=true 로 한 번만, 이후 visibility 토글 재진입은 incremental 로.
      void poll(!initialDone)
      initialDone = true
      timer = window.setInterval(() => poll(false), POLL_INTERVAL_MS)
    }
    const stop = () => {
      if (timer === null) return
      window.clearInterval(timer)
      timer = null
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        unreadRef.current = 0
        document.title = originalTitleRef.current
        start()
      } else {
        stop()
      }
    }

    if (document.visibilityState === 'visible') start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibility)
      stop()
      document.title = originalTitleRef.current
      unreadRef.current = 0
    }
  }, [session.id])

  // 새 메시지 도착 시 스크롤 맨 아래로
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length])

  const send = useCallback(async () => {
    const content = composer.trim()
    if (!content || sending || endedAt) return
    setSending(true)
    try {
      const res = await apiFetch<{ message: LiveMessage; agentJoinedAt: string }>(
        `/api/admin/chatbot-sessions/${session.id}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ content }),
        },
      )
      // 응답 메시지 즉시 표시 + 다음 폴링에서 시스템 메시지(상담 시작)도 따라옴
      lastCursorRef.current = res.message.createdAt
      setMessages((prev) => [...prev, res.message])
      setAgentJoinedAt(res.agentJoinedAt)
      setComposer('')
      onChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : '메시지 전송 실패')
    } finally {
      setSending(false)
    }
  }, [composer, sending, endedAt, session.id, onChanged])

  const end = useCallback(async () => {
    if (ending || endedAt) return
    if (!window.confirm('상담을 종료할까요? 이후 사용자는 해당 세션에 메시지를 보낼 수 없습니다.')) return
    setEnding(true)
    try {
      const res = await apiFetch<{ endedAt: string }>(
        `/api/admin/chatbot-sessions/${session.id}/end`,
        { method: 'POST' },
      )
      setEndedAt(res.endedAt)
      onChanged()
    } catch (err) {
      alert(err instanceof Error ? err.message : '종료 실패')
    } finally {
      setEnding(false)
    }
  }, [ending, endedAt, session.id, onChanged])

  const status = liveStatus({ escalatedAt: session.escalatedAt, agentJoinedAt, endedAt })
  const statusInfo = LIVE_STATUS_LABEL[status]
  const userLabel = session.userEmail ?? session.userName ?? '(unknown)'

  return (
    <div className={s.chatPanel}>
      <header className={s.chatHeader}>
        <div className={s.avatar}>{avatarInitial(session.userEmail)}</div>
        <div className={s.headerInfo}>
          <h3 className={s.headerTitle}>
            {userLabel} <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
          </h3>
          <div className={s.headerMeta}>
            <span>시작 {fmtDate(session.startedAt)}</span>
            {session.escalatedAt && <span>· 연결 {fmtTime(session.escalatedAt)}</span>}
            {agentJoinedAt && <span>· 응대 {fmtTime(agentJoinedAt)}</span>}
            {endedAt && <span>· 종료 {fmtTime(endedAt)}</span>}
            {session.escalatedInquiryId && (
              <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
                · inq {session.escalatedInquiryId.slice(0, 8)}…
              </span>
            )}
          </div>
        </div>
        {!endedAt && (
          <button
            type="button"
            onClick={end}
            disabled={ending}
            style={{
              padding: '6px 12px',
              fontSize: 13,
              borderRadius: 6,
              background: 'var(--ds-color-bg)',
              border: '1px solid var(--ds-color-border)',
              cursor: ending ? 'wait' : 'pointer',
            }}
          >
            {ending ? '종료 중…' : '상담 종료'}
          </button>
        )}
      </header>

      <div ref={scrollRef} className={s.messagesContainer}>
        {messages.map((m) => (
          <LiveMessageRow key={m.id} message={m} />
        ))}
        {pollError && (
          <div className={s.emptyState} style={{ color: 'crimson', fontSize: 12 }}>
            ⚠ 폴링 오류: {pollError} — 다음 시도까지 잠시만요
          </div>
        )}
      </div>

      <div className={s.composer}>
        <textarea
          className={s.composerInput}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send()
            }
          }}
          placeholder={endedAt ? '종료된 상담입니다' : '메시지 입력 — Enter 송신, Shift+Enter 줄바꿈'}
          rows={3}
          disabled={!!endedAt || sending}
        />
        <div className={s.composerActions}>
          <button
            type="button"
            onClick={send}
            disabled={!composer.trim() || sending || !!endedAt}
            className={s.sendButton}
          >
            {sending ? '전송 중…' : '전송'}
          </button>
        </div>
      </div>
    </div>
  )
}

function LiveMessageRow({ message }: { message: LiveMessage }) {
  if (message.role === 'system') {
    return (
      <div className={s.systemNote}>
        <span>{message.text}</span>
        <span style={{ fontSize: 11, color: 'var(--ds-color-text-secondary)' }}>
          {fmtTime(message.createdAt)}
        </span>
      </div>
    )
  }
  if (message.role === 'user') {
    return (
      <div className={`${s.row} ${s.rowUser}`}>
        <div className={`${s.bubble} ${s.bubbleUser}`}>{message.text}</div>
        <div className={s.timestamp}>{fmtTime(message.createdAt)}</div>
      </div>
    )
  }
  // agent (또는 assistant — escalation 이전 LLM 응답이 폴링에 섞여 올 가능성)
  const bubbleClass = message.role === 'agent' ? s.bubbleAgent : s.bubbleAssistant
  return (
    <div className={`${s.row} ${s.rowAssistant}`}>
      <div className={`${s.bubble} ${bubbleClass}`}>{message.text}</div>
      <div className={s.timestamp}>{fmtTime(message.createdAt)}</div>
    </div>
  )
}

// ============================================================
// ChatTranscript — 챗봇 전용/종료 세션의 읽기 전용 transcript
// ============================================================

function ChatTranscript({ detail }: { detail: SessionDetail }) {
  const { session, messages } = detail
  const escalatedAt = session.escalatedAt
  const userLabel = session.userEmail ?? session.userName ?? '(unknown)'
  const status = liveStatus(session)
  const statusInfo = LIVE_STATUS_LABEL[status]

  return (
    <div className={s.chatPanel}>
      <header className={s.chatHeader}>
        <div className={s.avatar}>{avatarInitial(session.userEmail)}</div>
        <div className={s.headerInfo}>
          <h3 className={s.headerTitle}>
            {userLabel} <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
          </h3>
          <div className={s.headerMeta}>
            <span>시작 {fmtDate(session.startedAt)}</span>
            {session.endedAt && <span>· 종료 {fmtDate(session.endedAt)}</span>}
            <span>· 메시지 {session.messageCount}</span>
          </div>
        </div>
        <div className={s.tokensMeta}>
          in {formatTokens(session.totalInputTokens)} / out {formatTokens(session.totalOutputTokens)}
          <br />
          cache-r {formatTokens(session.totalCacheReadTokens)} / w {formatTokens(session.totalCacheWriteTokens)}
        </div>
      </header>

      <div className={s.messagesContainer}>
        {messages.map((m, mIdx) => {
          const showEscalationBefore =
            !!escalatedAt &&
            new Date(m.createdAt).getTime() >= new Date(escalatedAt).getTime() &&
            (mIdx === 0 ||
              new Date(messages[mIdx - 1].createdAt).getTime() < new Date(escalatedAt).getTime())

          const textBlocks = m.content.filter(
            (b): b is { type: 'text'; text: string } => b.type === 'text',
          )
          const toolUseBlocks = m.content.filter(
            (b): b is { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> } =>
              b.type === 'tool_use',
          )
          const toolResultBlocks = m.content.filter(
            (b): b is { type: 'tool_result'; tool_use_id: string; content: string; is_error?: boolean } =>
              b.type === 'tool_result',
          )

          const hasText = textBlocks.length > 0
          const hasToolUse = toolUseBlocks.length > 0
          const hasToolResult = toolResultBlocks.length > 0

          return (
            <div key={m.id} style={{ display: 'contents' }}>
              {showEscalationBefore && (
                <div className={s.escalationDivider}>
                  <span>여기서 상담사 에스컬레이션 발생</span>
                </div>
              )}

              {m.role === 'system' && hasText && (
                <div className={s.systemNote}>
                  <span>{textBlocks.map((b) => b.text).join('\n')}</span>
                  <span style={{ fontSize: 11, color: 'var(--ds-color-text-secondary)' }}>
                    {fmtTime(m.createdAt)}
                  </span>
                </div>
              )}

              {m.role === 'user' && hasText && (
                <div className={`${s.row} ${s.rowUser}`}>
                  {textBlocks.map((b, idx) => (
                    <div key={`t-${idx}`} className={`${s.bubble} ${s.bubbleUser}`}>
                      {b.text}
                    </div>
                  ))}
                  <div className={s.timestamp}>{fmtTime(m.createdAt)}</div>
                </div>
              )}

              {(m.role === 'assistant' || m.role === 'agent') && hasText && (
                <div className={`${s.row} ${s.rowAssistant}`}>
                  {m.modelStopReason && m.role === 'assistant' && (
                    <div className={s.assistantMeta}>stop: {m.modelStopReason}</div>
                  )}
                  {textBlocks.map((b, idx) => (
                    <div
                      key={`t-${idx}`}
                      className={`${s.bubble} ${m.role === 'agent' ? s.bubbleAgent : s.bubbleAssistant}`}
                    >
                      {b.text}
                    </div>
                  ))}
                  <div className={s.timestamp}>{fmtTime(m.createdAt)}</div>
                </div>
              )}

              {hasToolUse && (
                <div className={`${s.row} ${s.rowSystem}`}>
                  {toolUseBlocks.map((b) => (
                    <div key={b.id} className={s.toolBlock}>
                      <div className={s.toolHeader}>
                        <span className={s.toolIcon}>🛠</span>
                        <span>tool_use</span>
                        <span className={s.toolName}>{b.name}</span>
                      </div>
                      <div className={s.toolBody}>{formatToolInput(b.input)}</div>
                    </div>
                  ))}
                </div>
              )}

              {hasToolResult && (
                <div className={`${s.row} ${s.rowSystem}`}>
                  {toolResultBlocks.map((b, idx) => (
                    <div
                      key={`tr-${idx}`}
                      className={`${s.toolBlock} ${b.is_error ? s.toolBlockError : ''}`}
                    >
                      <div className={`${s.toolHeader} ${b.is_error ? s.toolError : ''}`}>
                        <span className={s.toolIcon}>{b.is_error ? '⚠' : '✓'}</span>
                        <span>tool_result</span>
                      </div>
                      <div className={s.toolBody}>
                        {b.content.length > 1200 ? `${b.content.slice(0, 1200)}…` : b.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
